import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, Menu, X, Sparkles } from "lucide-react";
import { NAV_ORDER, TYPES } from "@/config";
import { SearchDialog } from "@/components/SearchDialog";
import { AskArchive } from "@/components/AskArchive";

export function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [askOpen, setAskOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <header
        data-testid="site-header"
        className={`sticky top-0 z-50 transition-colors duration-500 ${
          scrolled ? "bg-ink/85 backdrop-blur-xl border-b border-line" : "bg-transparent"
        }`}
      >
        <div className="mx-auto max-w-[1400px] px-5 lg:px-10">
          <div className="flex h-20 items-center justify-between gap-6">
            <Link to="/" data-testid="brand-link" className="flex items-baseline gap-3 shrink-0">
              <span className="font-serif text-3xl font-medium tracking-tight text-gold">COA</span>
              <span className="hidden sm:inline eyebrow text-faint">Crimes of Art</span>
            </Link>

            <nav className="hidden lg:flex items-center gap-7">
              {NAV_ORDER.map((k) => (
                <Link
                  key={k}
                  to={`/${TYPES[k].segment}`}
                  data-testid={`nav-${k}`}
                  className="eyebrow text-parchment/70 hover:text-gold transition-colors duration-300"
                >
                  {TYPES[k].label}
                </Link>
              ))}
              <Link
                to="/map"
                data-testid="nav-map"
                className="eyebrow text-parchment/70 hover:text-gold transition-colors duration-300"
              >
                Atlas
              </Link>
            </nav>

            <div className="flex items-center gap-2">
              <button
                data-testid="open-search"
                onClick={() => setSearchOpen(true)}
                aria-label="Search the archive"
                className="p-2 text-parchment/70 hover:text-gold transition-colors focus:outline-none focus:ring-2 focus:ring-gold"
              >
                <Search size={18} />
              </button>
              <button
                data-testid="open-ask-archive"
                onClick={() => setAskOpen(true)}
                className="hidden sm:inline-flex items-center gap-2 border border-gold/50 px-4 py-2 eyebrow text-gold hover:bg-gold/10 transition-colors focus:outline-none focus:ring-2 focus:ring-gold"
              >
                <Sparkles size={14} /> Ask the Archive
              </button>
              <button
                data-testid="open-mobile-menu"
                onClick={() => setMobileOpen(true)}
                aria-label="Menu"
                className="lg:hidden p-2 text-parchment/80 hover:text-gold"
              >
                <Menu size={22} />
              </button>
            </div>
          </div>
        </div>
      </header>

      {mobileOpen && (
        <div className="fixed inset-0 z-[60] bg-ink lg:hidden" data-testid="mobile-menu">
          <div className="flex h-20 items-center justify-between px-5">
            <span className="font-serif text-3xl text-gold">COA</span>
            <button onClick={() => setMobileOpen(false)} aria-label="Close menu" className="p-2 text-parchment">
              <X size={24} />
            </button>
          </div>
          <nav className="flex flex-col px-5 pt-6">
            {NAV_ORDER.map((k) => (
              <Link
                key={k}
                to={`/${TYPES[k].segment}`}
                onClick={() => setMobileOpen(false)}
                className="border-b border-line py-4 font-serif text-3xl text-parchment hover:text-gold"
              >
                {TYPES[k].label}
              </Link>
            ))}
            <Link
              to="/map"
              data-testid="nav-map-mobile"
              onClick={() => setMobileOpen(false)}
              className="border-b border-line py-4 font-serif text-3xl text-parchment hover:text-gold"
            >
              Atlas
            </Link>
            <button
              onClick={() => { setMobileOpen(false); setAskOpen(true); }}
              className="mt-8 inline-flex items-center gap-2 border border-gold px-5 py-3 eyebrow text-gold"
            >
              <Sparkles size={14} /> Ask the Archive
            </button>
          </nav>
        </div>
      )}

      <SearchDialog open={searchOpen} onClose={() => setSearchOpen(false)} />
      <AskArchive open={askOpen} onClose={() => setAskOpen(false)} />
    </>
  );
}
